import type { ReactNode } from 'react'

const EMPTYVALUE = '(empty)'

export type ChangeRecord = {
  fieldName: string
  before: string
  after: string
}

export type ChangeHistoryAction = 'create' | 'update' | 'duplicate' | 'delete'

function newValue(text: string): ReactNode {
  return <span className="bg-green-50 text-green-700">{text}</span>
}

function oldValue(text: string): ReactNode {
  return <span className="bg-red-50 text-red-700">{text}</span>
}

function tryParseJson(value: string): unknown | undefined {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

type Kind = 'empty' | 'number' | 'string' | 'object' | 'array' | 'other'

function isEmptyObject(val: unknown): boolean {
  return (
    val !== null &&
    typeof val === 'object' &&
    !Array.isArray(val) &&
    Object.keys(val as object).length === 0
  )
}

function isEmptyString(val: unknown): boolean {
  return typeof val === 'string' && val.trim().length === 0
}

function parseValue(raw: string): { kind: Kind; display: string } {
  if (raw == null || raw === '') {
    return { kind: 'empty', display: EMPTYVALUE }
  }

  const parsed = tryParseJson(raw)

  if (parsed === undefined) {
    if (isEmptyString(raw)) {
      return { kind: 'empty', display: EMPTYVALUE }
    }
    return { kind: 'string', display: raw }
  }

  if (typeof parsed === 'number') {
    return { kind: 'number', display: String(parsed) }
  }

  if (Array.isArray(parsed)) {
    if (!parsed.length) {
      return { kind: 'empty', display: EMPTYVALUE }
    }
    return { kind: 'array', display: JSON.stringify(parsed, null, 2) }
  }

  if (typeof parsed === 'object') {
    if (isEmptyObject(parsed)) {
      return { kind: 'empty', display: EMPTYVALUE }
    }
    return { kind: 'object', display: JSON.stringify(parsed, null, 2) }
  }

  return { kind: 'other', display: String(parsed) }
}

export function compareValues(change: ChangeRecord): {
  before: ReactNode | string
  after: ReactNode | string
} {
  const beforeV = parseValue(change.before)
  const afterV = parseValue(change.after)

  if (beforeV.kind === 'empty' && afterV.kind !== 'empty') {
    return { before: EMPTYVALUE, after: newValue(afterV.display) }
  }
  if (afterV.kind === 'empty' && beforeV.kind !== 'empty') {
    return { before: oldValue(beforeV.display), after: EMPTYVALUE }
  }

  // Simple display without diff highlighting
  return { before: beforeV.display, after: afterV.display }
}

function formatJsonValue(value: string) {
  if (!value) return EMPTYVALUE
  const parsed: unknown | undefined = tryParseJson(value)

  if (parsed === undefined || parsed === null) return value

  if (typeof parsed === 'number') {
    return String(parsed)
  }

  if (Array.isArray(parsed)) {
    if (!parsed.length) return EMPTYVALUE
  } else {
    if (isEmptyObject(parsed)) return EMPTYVALUE
  }

  return JSON.stringify(parsed, null, 2)
}

export function prepareValues(
  change: ChangeRecord,
  action: ChangeHistoryAction
) {
  let after: ReactNode | string = ''
  let before: ReactNode | string = ''

  switch (action) {
    case 'create':
    case 'duplicate':
      after = newValue(formatJsonValue(change.after) as string)
      before = change.before
      break
    case 'delete':
      after = change.after
      before = oldValue(formatJsonValue(change.before) as string)
      break
  }

  return { before, after }
}
